import { useState, useEffect, useCallback } from "react";
import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import FallRecordRow from "../components/FallRecordRow";
import VideoPlayer from "../components/VideoPlayer";
import SecureFullscreenVideo from "../components/SecureFullscreenVideo";
import FallsNotificationModal from "../components/FallsNotificationModal";
import calendarIcon from "../assets/calendar.svg";
import clockIcon from "../assets/clock.svg";
import hospitalIcon from "../assets/hospital.svg";
import seniorIcon from "../assets/senior.svg";
import videoIcon from "../assets/video.svg";
import viewAllIcon from "../assets/view_all.png";
import commentIcon from "../assets/comment.svg";
import fullscreenIcon from "../assets/fullscreen.svg";
import fallGraphIcon from "../assets/fall_graph.svg";
import TimePeriodSelector from "../components/TimePeriodSelector";
import "../styles/pages/FallsPage.css";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */
const W = "#ffffff";

/* ═══════════════════════ PAGE ═══════════════════════ */
export default function FallsPage({ onFiltersChange, residentId, showBackButton, onBackToResident, onViewAllModeChange }) {
  const [fallRecords, setFallRecords] = useState([]);
  const [fallAnalyticsData, setFallAnalyticsData] = useState([]);
  // TimePeriodSelector uses 'hourly'|'weekly'|'monthly'; map to API periods
  const [analyticsPeriod, setAnalyticsPeriod] = useState('hourly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFalls, setFilteredFalls] = useState([]);
  const [allFallsData, setAllFallsData] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const RECORDS_PER_PAGE = 3;
  const [selectedFall, setSelectedFall] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [videoProgress, setVideoProgress] = useState(42);
  // eslint-disable-next-line no-unused-vars
  const [currentTime, setCurrentTime] = useState("42:38");
  const [viewAllMode, setViewAllMode] = useState(!residentId);
  const [userClosedViewAll, setUserClosedViewAll] = useState(false);
  const [markingFalsePositive, setMarkingFalsePositive] = useState(false);
  const [showFalsePositiveConfirm, setShowFalsePositiveConfirm] = useState(false);
  const [notification, setNotification] = useState(null);
  const { isMobile } = useWindowSize();

  // Falls page filter states
  const [selectedAge, setSelectedAge] = useState("All");
  const [selectedRiskLevel, setSelectedRiskLevel] = useState("All");
  const [selectedSleepQuality, setSelectedSleepQuality] = useState("All");

  // Handle False Positive marking
  const handleMarkFalsePositive = async () => {
    if (!selectedFall) return;

    setMarkingFalsePositive(true);
    try {
      const response = await apiService.markFallAsFalsePositive(
        selectedFall.fall_id,
        "Admin marked this as not a real fall incident"
      );

      if (response.success) {
        console.log('[FallsPage] Fall marked as false positive successfully');
        setShowFalsePositiveConfirm(false);
        setSelectedFall(null);
        setVideoUrl(null);
        await loadFallData();
        setNotification({
          type: 'success',
          message: 'Fall marked as false positive and moved to training data'
        });
      }
    } catch (error) {
      console.error('[FallsPage] Failed to mark fall as false positive:', error);
      setNotification({
        type: 'error',
        message: 'Failed to mark fall as false positive. Please try again.'
      });
    } finally {
      setMarkingFalsePositive(false);
    }
  };

  // Expose filters to parent (Sidebar)
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        selectedAge,
        setSelectedAge,
        selectedRiskLevel,
        setSelectedRiskLevel,
        selectedSleepQuality,
        setSelectedSleepQuality,
      });
    }
  }, [selectedAge, selectedRiskLevel, selectedSleepQuality, onFiltersChange]);

  // Keyboard navigation for False Positive modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showFalsePositiveConfirm) {
          setShowFalsePositiveConfirm(false);
        } else if (showFullscreenModal) {
          setShowFullscreenModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showFalsePositiveConfirm, showFullscreenModal]);

  const loadFallData = useCallback(async (page = 1) => {
    if (!residentId && !viewAllMode && !userClosedViewAll) {
      setViewAllMode(true);
      if (onViewAllModeChange) {
        onViewAllModeChange(true);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (viewAllMode) {
        const fallsData = await apiService.getAllFalls(90, 1, 10000);
        const allFalls = fallsData.falls || [];

        setAllFallsData(allFalls);
        setFilteredFalls(allFalls);
        
        const totalFiltered = allFalls.length;
        const totalPagesFiltered = Math.ceil(totalFiltered / RECORDS_PER_PAGE);
        const startIdx = (page - 1) * RECORDS_PER_PAGE;
        const endIdx = startIdx + RECORDS_PER_PAGE;
        const paginatedFalls = allFalls.slice(startIdx, endIdx);
        
        setFallRecords(paginatedFalls);
        setCurrentPage(page);
        setTotalPages(totalPagesFiltered);
        setTotalCount(totalFiltered);
        
        // Map selector value to API period param
        const apiPeriod = analyticsPeriod === 'weekly' ? '7days' : analyticsPeriod === 'monthly' ? '30days' : 'hourly';
        const analyticsData = await apiService.getFallAnalytics(apiPeriod);
        setFallAnalyticsData(analyticsData.analytics || []);
      } else {
        const apiPeriod = analyticsPeriod === 'weekly' ? '7days' : analyticsPeriod === 'monthly' ? '30days' : 'hourly';
        const [fallsData, analyticsData] = await Promise.all([
          apiService.getAllFalls(90, 1, 10000),
          apiService.getFallAnalytics(apiPeriod, residentId)
        ]);

        let allFalls = fallsData.falls || [];
        let residentFalls = allFalls.filter(fall => fall.resident_id === residentId);
        
        setAllFallsData(residentFalls);
        setFilteredFalls(residentFalls);
        
        const totalFiltered = residentFalls.length;
        const totalPagesFiltered = Math.ceil(totalFiltered / RECORDS_PER_PAGE);
        const startIdx = (page - 1) * RECORDS_PER_PAGE;
        const endIdx = startIdx + RECORDS_PER_PAGE;
        const paginatedFalls = residentFalls.slice(startIdx, endIdx);

        setFallRecords(paginatedFalls);
        setCurrentPage(page);
        setTotalPages(totalPagesFiltered);
        setTotalCount(totalFiltered);
        setFallAnalyticsData(analyticsData.analytics || []);
      }
    } catch (err) {
      console.error('[FallsPage] Failed to load fall data:', err);
      console.error('[FallsPage] Resident ID that failed:', residentId);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [residentId, viewAllMode, RECORDS_PER_PAGE, onViewAllModeChange, userClosedViewAll, analyticsPeriod]);

  useEffect(() => { loadFallData(); }, [loadFallData]);
  
  const handleViewAll = () => {
    setViewAllMode(true);
    setUserClosedViewAll(false);
    setSelectedFall(null);
    setVideoUrl(null);
    setCurrentPage(1);
    if (onViewAllModeChange) {
      onViewAllModeChange(true);
    }
  };
  
  useEffect(() => {
    if (residentId) {
      setViewAllMode(false);
      setUserClosedViewAll(false);
      setCurrentPage(1);
      if (onViewAllModeChange) {
        onViewAllModeChange(false);
      }
    }
  }, [residentId, onViewAllModeChange]);
          
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      
      const startIdx = (newPage - 1) * RECORDS_PER_PAGE;
      const endIdx = startIdx + RECORDS_PER_PAGE;
      const paginatedFalls = filteredFalls.slice(startIdx, endIdx);
      
      setFallRecords(paginatedFalls);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFallRecordClick = async (fall) => {
    if (selectedFall?.fall_id === fall.fall_id) {
      setSelectedFall(null);
      setVideoUrl(null);
      loadFallData();
    } else {
      setSelectedFall(fall);
      
      setVideoLoading(true);
      try {
        const s3Key = fall.s3_key || fall.video_s3_key;
  
        console.log('[FallsPage] Fall data:', fall);
        console.log('[FallsPage] S3 Key:', s3Key);
  
        if (!s3Key) {
          console.warn('[FallsPage] No S3 key found in fall data');
          setVideoUrl(null);
          setVideoLoading(false);
          return;
        }
  
        const cloudFrontUrl = `https://video.ait.care/${s3Key}`;
  
        console.log('[FallsPage] Generated CloudFront URL:', cloudFrontUrl);
        console.log('[FallsPage] Checking cookies...');
        console.log('[FallsPage] All cookies:', document.cookie);
        
        setVideoUrl(cloudFrontUrl);
      } catch (error) {
        console.error('[FallsPage] Error generating video URL:', error);
        setVideoUrl(null);
      } finally {
        setVideoLoading(false);
      }
    }
  };

  // Client-side search filtering
  useEffect(() => {
    if (!allFallsData || allFallsData.length === 0) return;

    let filtered = [...allFallsData];

    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(fall => {
        const residentName = (fall.resident_name || '').toLowerCase();
        const roomId = (fall.location_id || fall.room_id || '').toLowerCase();
        const fallId = (fall.fall_id || '').toLowerCase();
        const severity = (fall.severity || '').toLowerCase();
        
        return residentName.includes(query) || 
               roomId.includes(query) || 
               fallId.includes(query) ||
               severity.includes(query);
      });
    }

    if (selectedRiskLevel && selectedRiskLevel !== 'All') {
      filtered = filtered.filter(fall => {
        const severity = (fall.severity || '').toUpperCase();
        return severity === selectedRiskLevel.toUpperCase();
      });
    }

    setFilteredFalls(filtered);

    const totalFiltered = filtered.length;
    const totalPagesFiltered = Math.ceil(totalFiltered / RECORDS_PER_PAGE) || 1;
    
    setCurrentPage(1);
    
    const startIdx = 0;
    const endIdx = RECORDS_PER_PAGE;
    const paginatedFalls = filtered.slice(startIdx, endIdx);
    
    setFallRecords(paginatedFalls);
    setTotalPages(totalPagesFiltered);
    setTotalCount(totalFiltered);
  }, [searchQuery, selectedRiskLevel, allFallsData, RECORDS_PER_PAGE]);

  // Format date and time in UTC (facility timezone)
  const formatDateTime = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: 'N/A' };
    try {
      const dt = new Date(timestamp);
      const year = dt.getUTCFullYear();
      const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dt.getUTCDate()).padStart(2, '0');
      const hours = String(dt.getUTCHours()).padStart(2, '0');
      const minutes = String(dt.getUTCMinutes()).padStart(2, '0');
      const seconds = String(dt.getUTCSeconds()).padStart(2, '0');
      
      const date = `${day}-${month}-${year}`;
      const time = `${hours}:${minutes}:${seconds}`;
      return { date, time };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Show error in a centered view if there's an actual error
  if (error && residentId) {
    return (
      <main className="falls-page falls-page--error-state">
        {showBackButton && onBackToResident && (
          <button onClick={onBackToResident} className="falls-back-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5" strokeLinecap="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Resident Details
          </button>
        )}
        <ErrorMessage error={error} onRetry={loadFallData} residentId={residentId} />
      </main>
    );
  }

  // Calculate max falls for chart scaling (dynamic Y-axis)
  const maxFalls = fallAnalyticsData.length > 0 
    ? Math.max(...fallAnalyticsData.map(d => d.falls), 5)
    : 5;
  
  const yAxisMax = Math.ceil(maxFalls / 5) * 5;
  const yAxisLabels = Array.from({length: 6}, (_, i) => Math.floor(yAxisMax * (5-i) / 5));

  return (
    <>
            <main
        style={{
          flex: 1,
          padding: isMobile ? "10px 10px" : "12px 60px",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minWidth: 0,
          fontFamily: "'Segoe UI', sans-serif",
          color: W,
          position: "relative",
          zIndex: 1,
          paddingTop: isMobile ? 75 : (showBackButton ? 140 : 82),
          paddingBottom: isMobile ? 10 : 12,
          height: "100vh",
          boxSizing: "border-box",
        }}
      >
                {/* Back to Resident Details Button */}
        {showBackButton && onBackToResident && (
          <button onClick={onBackToResident} className="falls-back-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5" strokeLinecap="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Resident Details
          </button>
        )}

        {/* Search Bar with Fall Report and View All */}
        <div className={`falls-search-bar ${isMobile ? 'mobile' : ''}`}>
          {viewAllMode && (
            <div className="falls-view-all-indicator">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a90e2" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="M16 11h6M19 8v6"/>
              </svg>
              <span className="falls-view-all-indicator__text">Viewing All Residents with Falls</span>
              <button
                onClick={() => {
                  setViewAllMode(false);
                  setUserClosedViewAll(true);
                  setSelectedFall(null);
                  setVideoUrl(null);
                  setCurrentPage(1);
                  setSearchQuery("");
                  setFallRecords([]);
                  setAllFallsData([]);
                  setFilteredFalls([]);
                  setTotalCount(0);
                  setTotalPages(1);
                  if (onViewAllModeChange) {
                    onViewAllModeChange(false);
                  }
                }}
                className="falls-view-all-indicator__close"
                title="Clear View All"
              >
                ×
              </button>
            </div>
          )}
          
          {!isMobile && (
            <button className={`falls-report-button ${isMobile ? 'mobile' : ''}`}>
              Fall Report
            </button>
          )}

          <div className={`falls-search-input-wrapper ${isMobile ? 'mobile' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="falls-search-input"
            />
          </div>

          <button
            onClick={handleViewAll}
            className={`falls-view-all-button ${viewAllMode ? 'falls-view-all-button--active' : ''}`}
          >
            View All
            <img src={viewAllIcon} alt="view all" />
          </button>
        </div>

        {/* Table Section - FIXED HEIGHT WRAPPER */}
        <div className="falls-table-section">
          {/* Table Headers - Hide on mobile */}
          {!isMobile && (
            <div className={`falls-table-headers ${isMobile ? 'mobile' : ''}`}>
              <div className="falls-table-header">
                <img src={calendarIcon} alt="calendar" />
                <img src={clockIcon} alt="clock" />
                <span>Date & Time</span>
              </div>
              <div className="falls-table-header">
                <img src={seniorIcon} alt="senior" />
                <span>Senior</span>
              </div>
              <div className="falls-table-header falls-table-header--room">
                <img src={hospitalIcon} alt="room" />
                <span>Room No</span>
              </div>
              <div className="falls-table-header falls-table-header--video">
                <img src={videoIcon} alt="video" />
                <span>Video</span>
              </div>
            </div>
          )}

          {/* Table Rows Container - FIXED HEIGHT */}
          <div className="falls-table-rows">
            {loading && fallRecords.length === 0 ? (
              <div className="falls-table-loading">
                <LoadingSpinner message="Loading fall data..." />
              </div>
            ) : !residentId && !viewAllMode ? (
              <div className="falls-table-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <p className="falls-table-empty__title">No Resident Selected</p>
                <p className="falls-table-empty__subtitle">Please select a resident from the dropdown</p>
              </div>
            ) : totalCount === 0 ? (
              <div className="falls-table-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                <p className="falls-table-empty__title">No Fall Incidents Found</p>
                <p className="falls-table-empty__subtitle">No fall records for this resident</p>
              </div>
            ) : (
              <>
                {loading && fallRecords.length > 0 && (
                  <div className="falls-table-loading-overlay">
                    <LoadingSpinner message="Updating..." />
                  </div>
                )}
                {fallRecords.map((record) => (
                  <FallRecordRow
                    key={record.fall_id || record.fall_ts}
                    record={record}
                    isSelected={selectedFall?.fall_id === record.fall_id}
                    isMobile={isMobile}
                    onRecordClick={handleFallRecordClick}
                    formatDateTime={formatDateTime}
                  />
                ))}
              </>
            )}
          </div>
          
          {/* Pagination Controls */}
          <div className="falls-pagination-container">
            {!loading && totalCount > 0 && totalPages > 1 && (
              <div className="falls-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="falls-pagination__nav-btn"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Prev
                </button>

                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  const showPage = pageNum === 1 || 
                                   pageNum === 2 ||
                                   pageNum === totalPages || 
                                   pageNum === totalPages - 1 ||
                                   Math.abs(pageNum - currentPage) <= 1;
                  
                  if (!showPage) {
                    if (pageNum === currentPage - 2 && currentPage > 4) {
                      return <span key={pageNum} className="falls-pagination__ellipsis">...</span>;
                    }
                    if (pageNum === currentPage + 2 && currentPage < totalPages - 3) {
                      return <span key={pageNum} className="falls-pagination__ellipsis">...</span>;
                    }
                    return null;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`falls-pagination__page-btn ${pageNum === currentPage ? 'falls-pagination__page-btn--active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="falls-pagination__nav-btn"
                >
                  Next
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>

                <span className="falls-pagination__info">({totalCount} total)</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - Analytics and Videos - FIXED HEIGHT */}
        <div className={`falls-bottom-section ${isMobile ? 'mobile' : ''}`}>
          {/* Fall Analytics */}
          <div className="falls-analytics-card">
            <div className="falls-analytics-card__header">
              <h3 className="falls-analytics-card__title">
                Fall Analytics {viewAllMode && <span className="falls-analytics-card__subtitle">(All Residents)</span>}
              </h3>
              <TimePeriodSelector
                value={analyticsPeriod}
                onChange={setAnalyticsPeriod}
                disabled={loading}
                ariaLabel="Fall analytics time period"
              />
            </div>

            {/* Chart - Stem Chart with Y-axis labels */}
            <div className="falls-analytics-chart">
              {/* Y-Axis with dynamic labels */}
              <div className="falls-analytics-chart__y-axis">
                {yAxisLabels.map((num, idx) => (
                  <div key={idx} className="falls-analytics-chart__y-label">{num}</div>
                ))}
              </div>

              {/* Y-Axis Line */}
              <div className="falls-analytics-chart__y-line" />
  
              {/* X-Axis Line */}
              <div className="falls-analytics-chart__x-line" />

              {/* Chart Content */}
              <div className="falls-analytics-chart__content">
                {(!residentId && !viewAllMode) || fallAnalyticsData.length === 0 ? (
                  <div className="falls-analytics-chart__empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
                      <path d="M3 3v18h18"/>
                      <path d="M18 17V9"/>
                      <path d="M13 17V5"/>
                      <path d="M8 17v-3"/>
                    </svg>
                    <p>{!residentId && !viewAllMode ? "Select a resident to view analytics" : "No analytics data available"}</p>
                  </div>
                ) : fallAnalyticsData.map((data, idx) => {
                  const heightPercent = (data.falls / yAxisMax) * 100;
                  const hasFalls = data.falls > 0;
                  
                  return (
                    <div key={idx} className="falls-analytics-chart__bar">
                      {hasFalls && (
                        <div 
                          className="falls-analytics-chart__bar-count falls-analytics-chart__bar-count--dynamic"
                          style={{ '--bar-count-top': `calc(${100 - heightPercent}% - 50px)` }}
                        >
                          {data.falls}
                        </div>
                      )}
                      
                      <div className={`falls-analytics-chart__bar-circle ${hasFalls ? 'falls-analytics-chart__bar-circle--active' : 'falls-analytics-chart__bar-circle--inactive'}`}>
                        <img
                          src={fallGraphIcon}
                          alt="fall"
                          className={hasFalls ? 'falls-chart-icon--active' : 'falls-chart-icon--inactive'}
                        />
                      </div>
          
                      {hasFalls && (
                        <div 
                          className="falls-analytics-chart__bar-stem falls-analytics-chart__bar-stem--dynamic" 
                          style={{ '--bar-stem-height': `${heightPercent}%` }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* X-axis labels — show every Nth label for readability */}
              <div className="falls-analytics-chart__x-labels">
                {fallAnalyticsData.map((data, idx) => {
                  // Determine label spacing: hourly=every 3rd, weekly=all, monthly=every 5th
                  const total = fallAnalyticsData.length;
                  let showLabel = true;
                  if (total > 10) {
                    // For 24 hourly buckets, show every 3rd (9AM, 12PM, 3PM...)
                    // For 30 daily buckets, show every 5th
                    const step = total > 20 ? 3 : 5;
                    showLabel = idx % step === 0 || idx === total - 1;
                  }
                  return (
                    <div key={idx} className="falls-analytics-chart__x-label">
                      {showLabel ? (data.label || data.time) : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Fall Videos - FIXED HEIGHT */}
          <div className="falls-video-card">
            <div className="falls-video-card__header">
              <div className="falls-video-card__title-section">
                <h3>Fall Video</h3>
                {selectedFall && (
                  <p>{selectedFall.resident_name} - {formatDateTime(selectedFall.fall_ts).date} {formatDateTime(selectedFall.fall_ts).time}</p>
                )}
              </div>
              <div className="falls-video-card__actions">
                {selectedFall && (
                  <div className="falls-false-positive-toggle">
                    <span className="falls-false-positive-toggle__label">False Positive</span>
                    <div
                      onClick={() => !markingFalsePositive && setShowFalsePositiveConfirm(true)}
                      className={`falls-false-positive-toggle__switch ${markingFalsePositive ? 'falls-false-positive-toggle__switch--disabled' : 'falls-false-positive-toggle__switch--enabled'}`}
                      title="Click to mark this fall as a false positive"
                    >
                      <div className="falls-false-positive-toggle__circle" />
                    </div>
                  </div>
                )}
                <img src={commentIcon} alt="comment" className="falls-video-card__action-icon" />
                <img
                  src={fullscreenIcon}
                  alt="fullscreen"
                  onClick={() => setShowFullscreenModal(true)}
                  className={`falls-video-card__action-icon ${selectedFall ? 'falls-video-card__action-icon--enabled' : 'falls-video-card__action-icon--disabled'}`}
                />
              </div>
            </div>

            {videoLoading ? (
              <div className="falls-video-empty">
                <div className="falls-video-empty__content">
                  <LoadingSpinner message="Loading video..." />
                </div>
              </div>
            ) : videoUrl && selectedFall ? (
              <VideoPlayer videoUrl={videoUrl} fallInfo={selectedFall} />
            ) : (
              <div className="falls-video-empty">
                <div className="falls-video-empty__content">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="1.5">
                    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                    <line x1="7" y1="2" x2="7" y2="22"/>
                    <line x1="17" y1="2" x2="17" y2="22"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                  </svg>
                  <p>{selectedFall ? 'Video not available for this fall event' : 'Click on a fall incident to view video'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Fullscreen Video Modal - Secure Version */}
      {showFullscreenModal && (
        <SecureFullscreenVideo
          videoUrl={videoUrl}
          fallInfo={selectedFall}
          onClose={() => setShowFullscreenModal(false)}
          formatDateTime={formatDateTime}
        />
      )}

      {/* False Positive Confirmation Modal */}
      {showFalsePositiveConfirm && (
        <div className="falls-false-positive-modal">
          <div className="falls-false-positive-modal__content">
            <h3 className="falls-false-positive-modal__title">⚠️ Mark as False Positive?</h3>
            <p className="falls-false-positive-modal__description">
              Are you sure you want to mark this fall as a false positive?
            </p>
            <div className="falls-false-positive-modal__warning">
              <p>
                <strong>This action will:</strong><br />
                • Move the video to training data bucket<br />
                • Remove the fall from the dashboard<br />
                • Delete the fall record from the database<br />
                • <strong>This action cannot be undone</strong>
              </p>
            </div>
            {selectedFall && (
              <div className="falls-false-positive-modal__info">
                <p><strong>Resident:</strong> {selectedFall.resident_name}</p>
                <p><strong>Date:</strong> {formatDateTime(selectedFall.fall_ts).date}</p>
                <p><strong>Time:</strong> {formatDateTime(selectedFall.fall_ts).time}</p>
              </div>
            )}
            <div className="falls-false-positive-modal__actions">
              <button
                onClick={() => setShowFalsePositiveConfirm(false)}
                disabled={markingFalsePositive}
                className="falls-false-positive-modal__button falls-false-positive-modal__button--cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkFalsePositive}
                disabled={markingFalsePositive}
                className="falls-false-positive-modal__button falls-false-positive-modal__button--confirm"
              >
                {markingFalsePositive ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5" className="spinning">
                      <circle cx="12" cy="12" r="10" opacity="0.3"/>
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={W} strokeWidth="2.5">
                      <path d="M5 13l4 4L19 7"/>
                    </svg>
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notification && (
        <FallsNotificationModal
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
}
